import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import AIAssistantModal from "@/components/ai/AIAssistantModal";

/**
 * Assistant IA — point d'entrée unique côté frontend.
 *
 * Même patron que ContactModalProvider (un provider monté à la racine, un
 * hook pour l'ouvrir depuis n'importe où), avec une différence : l'ouverture
 * renvoie une PROMESSE, résolue avec le résultat appliqué par l'utilisateur,
 * ou `null` s'il ferme sans appliquer. La page appelante n'a donc ni
 * callback à câbler ni état à gérer :
 *
 *   const ai = useAIAssistant();
 *   const result = await ai.open({ entity: "product", data: product });
 *   if (result) setForm(f => ({ ...f, description: result.content }));
 *
 * La modale ne touche JAMAIS aux données métier (§12 du cahier des charges) :
 * elle propose, la page applique et enregistre.
 */
const AIAssistantContext = createContext(null);

export const useAIAssistant = () => {
  const ctx = useContext(AIAssistantContext);
  if (!ctx) {
    // Erreur de câblage, pas d'exécution : mieux vaut le dire franchement
    // que renvoyer un objet muet dont les appels ne feraient rien.
    throw new Error("useAIAssistant doit être utilisé à l'intérieur de <AIAssistantProvider>");
  }
  return ctx;
};

export function AIAssistantProvider({ children }) {
  const [request, setRequest] = useState(null);
  // La promesse en cours : conservée hors du state pour ne pas déclencher de
  // rendu, et toujours résolue exactement une fois (voir `settle`).
  const pending = useRef(null);

  const settle = useCallback((value) => {
    const resolve = pending.current;
    pending.current = null;
    setRequest(null);
    if (resolve) resolve(value);
  }, []);

  const open = useCallback((options = {}) => {
    // Une seconde ouverture alors qu'une modale est déjà là annulerait la
    // première sans que personne ne l'apprenne : on résout l'ancienne
    // promesse à `null` plutôt que de la laisser en suspens pour toujours.
    if (pending.current) settle(null);
    return new Promise((resolve) => {
      pending.current = resolve;
      setRequest({
        entity: options.entity || null,
        action: options.action || null,
        allowedActions: options.allowedActions || null,
        data: options.data || {},
        title: options.title || null,
        // Champs libres proposés en plus du contexte (ex : ton, longueur).
        fields: options.fields || null,
      });
    });
  }, [settle]);

  const value = useMemo(() => ({ open, close: () => settle(null) }), [open, settle]);

  return (
    <AIAssistantContext.Provider value={value}>
      {children}
      {request && (
        <AIAssistantModal
          request={request}
          onApply={(result) => settle(result)}
          onCancel={() => settle(null)}
        />
      )}
    </AIAssistantContext.Provider>
  );
}

export default AIAssistantProvider;
